import React, { useState, useEffect } from 'react';
import { useVoiceRecognition } from '../lib/useVoiceRecognition';
import { getMascotConfig } from '../lib/mascotConfig';
import { getModuleResponse, type ModuleResponse } from '../lib/moduleResponses';

/**
 * VoiceMascot - Enterprise-grade AI voice assistant with Penguin mascot
 *
 * Features:
 * - Web Speech API integration for voice recognition
 * - State-based animations (idle, listening, processing)
 * - Real-time voice transcription
 * - Module-specific pre-canned queries
 * - Penguin mascot with animated states
 */

interface VoiceMascotProps {
  currentModule?: string;
  onQuerySubmit?: (query: string) => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  className?: string;
}

type MascotState = 'idle' | 'listening' | 'processing' | 'speaking';

const VoiceMascot: React.FC<VoiceMascotProps> = ({
  currentModule = 'dashboard',
  onQuerySubmit,
  position = 'bottom-right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mascotState, setMascotState] = useState<MascotState>('idle');
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<ModuleResponse | null>(null);
  const [showResponse, setShowResponse] = useState(false);

  const {
    transcript,
    isListening,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useVoiceRecognition();

  const config = getMascotConfig(currentModule);

  // Update mascot state based on listening status
  useEffect(() => {
    if (isListening) {
      setMascotState('listening');
      setShowTranscript(true);
    } else if (transcript) {
      setMascotState('processing');
      // Process transcript after a short delay
      const timer = setTimeout(() => {
        if (onQuerySubmit && transcript) {
          onQuerySubmit(transcript);
        }
        setMascotState('idle');
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setMascotState('idle');
    }
  }, [isListening, transcript, onQuerySubmit]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      stopListening();
      setShowTranscript(false);
    }
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      resetTranscript();
      startListening();
    }
  };

  const handlePreCannedQuery = (query: string) => {
    setMascotState('processing');

    // Get context-aware response for this module and query
    const response = getModuleResponse(currentModule, query);

    if (response) {
      setCurrentResponse(response);
      setShowResponse(true);
      setMascotState('speaking');

      // Auto-close response after 30 seconds
      setTimeout(() => {
        setShowResponse(false);
        setMascotState('idle');
      }, 30000);
    } else {
      // Fallback to original handler if no response found
      if (onQuerySubmit) {
        onQuerySubmit(query);
      }
      setTimeout(() => setMascotState('idle'), 1500);
    }
  };

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
  };

  if (!isSupported) {
    return null; // Hide mascot if Web Speech API is not supported
  }

  return (
    <div className={`fixed ${positionClasses[position]} z-50 ${className}`}>
      {/* Expanded Panel */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">FinCopilot Assistant</h3>
                <p className="text-blue-100 text-xs">{config.name}</p>
              </div>
              <button
                onClick={handleToggle}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                aria-label="Close assistant"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Transcript Display */}
          {showTranscript && transcript && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-start space-x-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs text-blue-600 font-semibold mb-1">You said:</p>
                  <p className="text-sm text-gray-800">{transcript}</p>
                </div>
              </div>
            </div>
          )}

          {/* Pre-Canned Queries */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Quick Actions
            </h4>
            <div className="space-y-2">
              {config.queries.map((query, index) => (
                <button
                  key={index}
                  onClick={() => handlePreCannedQuery(query.text)}
                  className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-all group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                        {query.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{query.description}</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Voice Control Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isListening ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-xs text-gray-600">
                  {isListening ? 'Listening...' : 'Ready'}
                </span>
              </div>
              <button
                onClick={handleVoiceToggle}
                className={`flex items-center justify-center p-3 rounded-lg transition-all ${
                  isListening
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                aria-label={isListening ? 'Stop listening' : 'Start voice input'}
              >
                {isListening ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mascot Button */}
      <button
        onClick={handleToggle}
        className="relative group"
        aria-label="Toggle voice assistant"
      >
        {/* Outer Ring Animation (when listening) */}
        {isListening && (
          <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-75" />
        )}

        {/* Mascot Container */}
        <div className="relative w-16 h-16 bg-white rounded-full shadow-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all overflow-hidden">
          <MascotAvatar state={mascotState} />
        </div>

        {/* Status Indicator */}
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white shadow-md">
          {mascotState === 'listening' && (
            <div className="w-full h-full rounded-full bg-red-500 animate-pulse" />
          )}
          {mascotState === 'processing' && (
            <div className="w-full h-full rounded-full bg-blue-500 animate-spin-slow" />
          )}
          {mascotState === 'idle' && (
            <div className="w-full h-full rounded-full bg-green-500" />
          )}
        </div>
      </button>

      {/* Response Modal */}
      {showResponse && currentResponse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-fade-in">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-2xl">🐧</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">FinCopilot Response</h3>
                  <p className="text-blue-100 text-xs">{config.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowResponse(false);
                  setMascotState('idle');
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                aria-label="Close response"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Query */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Your Query</p>
                    <p className="text-sm text-gray-800 font-medium">{currentResponse.query}</p>
                  </div>
                </div>
              </div>

              {/* Response */}
              <div className="space-y-4">
                {currentResponse.response.split('\n').map((line, idx) => {
                  // Empty line
                  if (line.trim() === '') return <div key={idx} className="h-2" />;

                  // Main heading with dash separator (e.g., "**Title** - Subtitle:")
                  if (line.match(/^\*\*.*\*\*\s+-\s+.*/)) {
                    const match = line.match(/^\*\*(.*?)\*\*\s+-\s+(.*)/);
                    if (match) {
                      return (
                        <div key={idx} className="pb-3 border-b border-gray-200 mb-3">
                          <h3 className="text-lg font-bold text-gray-900">{match[1]}</h3>
                          <p className="text-sm text-gray-600 mt-1">{match[2]}</p>
                        </div>
                      );
                    }
                  }

                  // Subheading (e.g., "**Current State:**")
                  if (line.match(/^\*\*.*:\*\*$/)) {
                    const text = line.replace(/\*\*/g, '').replace(':', '');
                    return <h4 key={idx} className="text-base font-bold text-gray-900 mt-4 mb-2">{text}</h4>;
                  }

                  // Bold text inline (e.g., "**Annual Savings: $2.9M**")
                  if (line.match(/\*\*.*\*\*/)) {
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                      <p key={idx} className="text-sm text-gray-700 leading-relaxed">
                        {parts.map((part, i) => {
                          if (part.match(/^\*\*.*\*\*$/)) {
                            return <strong key={i} className="font-bold text-gray-900">{part.replace(/\*\*/g, '')}</strong>;
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </p>
                    );
                  }

                  // Bullet point (e.g., "• Item")
                  if (line.match(/^[•·]/)) {
                    const text = line.replace(/^[•·]\s*/, '');
                    return (
                      <div key={idx} className="flex items-start space-x-2 ml-4">
                        <span className="text-blue-600 font-bold mt-1">•</span>
                        <p className="text-sm text-gray-700 flex-1">{text}</p>
                      </div>
                    );
                  }

                  // Regular paragraph
                  return <p key={idx} className="text-sm text-gray-700 leading-relaxed">{line}</p>;
                })}
              </div>

              {/* Metrics */}
              {currentResponse.data?.metrics && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Key Metrics
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(currentResponse.data.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">{key}</p>
                        <p className="text-lg font-bold text-gray-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {currentResponse.data?.recommendations && currentResponse.data.recommendations.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recommendations
                  </h4>
                  <ul className="space-y-2">
                    {currentResponse.data.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-green-600 font-bold flex-shrink-0">•</span>
                        <span className="text-sm text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Insights */}
              {currentResponse.data?.insights && currentResponse.data.insights.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Key Insights
                  </h4>
                  <ul className="space-y-2">
                    {currentResponse.data.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-purple-600 font-bold flex-shrink-0">💡</span>
                        <span className="text-sm text-gray-700">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowResponse(false);
                  setMascotState('idle');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * MascotAvatar - Penguin mascot with state-based animations
 */
interface MascotAvatarProps {
  state: MascotState;
}

const MascotAvatar: React.FC<MascotAvatarProps> = ({ state }) => {
  const getAnimationClass = () => {
    switch (state) {
      case 'idle':
        return 'animate-breathe';
      case 'listening':
        return 'animate-pulse-grow';
      case 'processing':
        return 'animate-spin-slow';
      case 'speaking':
        return 'animate-bounce-subtle';
      default:
        return '';
    }
  };

  // Penguin mascot as inline base64 image data URL
  const penguinImageData = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 220'%3E%3Cdefs%3E%3ClinearGradient id='body' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%231e3a8a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%232563eb;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Cellipse cx='100' cy='200' rx='60' ry='8' fill='%23000' opacity='0.2'/%3E%3Cpath d='M 100 60 Q 70 60 55 90 L 55 150 Q 55 180 70 195 L 130 195 Q 145 180 145 150 L 145 90 Q 130 60 100 60 Z' fill='url(%23body)' stroke='%23000' stroke-width='3'/%3E%3Cpath d='M 80 120 Q 75 130 80 140 L 100 170 L 120 140 Q 125 130 120 120 Z' fill='%23fff' stroke='%23000' stroke-width='3'/%3E%3Cellipse cx='85' cy='90' rx='18' ry='22' fill='%23fff' stroke='%23000' stroke-width='3'/%3E%3Cellipse cx='115' cy='90' rx='18' ry='22' fill='%23fff' stroke='%23000' stroke-width='3'/%3E%3Ccircle cx='88' cy='92' r='7' fill='%23000'/%3E%3Ccircle cx='112' cy='92' r='7' fill='%23000'/%3E%3Ccircle cx='90' cy='89' r='3' fill='%23fff'/%3E%3Ccircle cx='114' cy='89' r='3' fill='%23fff'/%3E%3Cellipse cx='100' cy='110' rx='10' ry='8' fill='%23f59e0b' stroke='%23000' stroke-width='2'/%3E%3Cpath d='M 95 110 L 85 115 L 95 115 Z' fill='%23f59e0b' stroke='%23000' stroke-width='2'/%3E%3Cpath d='M 105 110 L 115 115 L 105 115 Z' fill='%23f59e0b' stroke='%23000' stroke-width='2'/%3E%3Cpath d='M 140 70 Q 155 65 165 75 L 160 90 Q 150 85 145 85 Z' fill='%232563eb' stroke='%23000' stroke-width='3'/%3E%3Cpath d='M 60 70 Q 45 65 35 75 L 40 90 Q 50 85 55 85 Z' fill='%232563eb' stroke='%23000' stroke-width='3'/%3E%3Crect x='60' y='185' width='30' height='18' rx='3' fill='%23f59e0b' stroke='%23000' stroke-width='2'/%3E%3Crect x='110' y='185' width='30' height='18' rx='3' fill='%23f59e0b' stroke='%23000' stroke-width='2'/%3E%3C/svg%3E`;

  return (
    <div className={`w-full h-full flex items-center justify-center ${getAnimationClass()}`}>
      <img
        src={penguinImageData}
        alt="Penguin mascot"
        className="w-full h-full object-contain"
      />

      {/* Sound Waves (when listening or speaking) */}
      {(state === 'listening' || state === 'speaking') && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="absolute -left-4 top-1/2 transform -translate-y-1/2">
            <div className="w-2 h-4 bg-blue-500 rounded-full opacity-50 animate-pulse" />
          </div>
          <div className="absolute -right-4 top-1/2 transform -translate-y-1/2">
            <div className="w-2 h-4 bg-blue-500 rounded-full opacity-50 animate-pulse" />
          </div>
        </div>
      )}

      {/* Energy Lines (when processing) */}
      {state === 'processing' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 border-2 border-blue-500 rounded-full animate-ping opacity-50" />
        </div>
      )}
    </div>
  );
};

export default VoiceMascot;
